<template>
  <div>
    <DataTable
      :value="detenidos"
      :loading="isLoading"
      :lazy="true"
      :first="offset"
      :rows="pageSize"
      :totalRecords="totalDocs"
      @page="handlePageChange"
      :resizableColumns="false"
      :paginator="false"
      class="center-screen shadow-2"
      paginatorTemplate="PrevPageLink PageLinks NextPageLink"
      style="border-radius: 6px; min-height: 460px"
    >
      <template #empty> Aún no hay detenidos registrados. </template>

      <Column field="NumeroIdentificacion" header="DNI" />
      <Column field="NumeroActuacion" header="N° Actuación" />
      <!-- <Column field="CausaCaratula" header="Carátula/Causa" /> -->
      <Column field="UfiOJuzgado" header="Ufi/Juzgado" />
      <Column field="fechaIngreso" header="Fecha de ingreso">
        <template #body="{ data }">
          {{ formatDate(data.FechaIngreso) }}
        </template>
      </Column>
      <!-- <Column field="EstadoVisual" header="Estado de salud" /> -->
      <Column v-if="tipoUsuario === 'Super User'" field="DependenciaNombre" header="Dependencia" />
      <Column field="EstadoActual" header="Estado">
        <template #body="{ data }">
          <Chip
            :label="estadoLabels[data.EstadoActual]"
            :class="['estado-chip', estadoChipClass(data.EstadoActual)]"
          />
        </template>
      </Column>
      <Column header="Acciones">
        <template #body="{ data }">
          <div class="w-full flex justify-content-center">
            <Button class="seeButton" @click="verDetalle(data)">
              <div class="mt-1">
                <Eye color="#0B5195" :size="20" />
              </div>
            </Button>
          </div>
        </template>
      </Column>
    </DataTable>
    <div class="flex justify-content-center mt-2" v-if="detenidos.length">
      <ul
        class="flex list-none border-round-xl overflow-hidden gap-2 border-gray px-3 text-custom-gray"
      >
        <li
          class="px-3 py-2 my-1 border-gray border-round-xl"
          :class="canGoPrev ? 'cursor-pointer hover:bg-gray-200' : 'cursor-not-allowed opacity-40'"
          @click="canGoPrev && prevPage()"
        >
          <i class="pi pi-angle-left"></i>
        </li>
        <li
          v-for="page in visiblePages"
          :key="page"
          class="cursor-pointer px-3 py-2 my-1 border-gray hover:opacity-20 border-round-xl"
          :class="[page === currentPage ? 'selected' : 'hover:opacity-20']"
          @click="goToPage(page)"
        >
          {{ page }}
        </li>
        <li
          class="px-3 py-2 my-1 border-gray border-round-xl"
          :class="canGoNext ? 'cursor-pointer hover:bg-gray-200' : 'cursor-not-allowed opacity-40'"
          @click="canGoNext && nextPage()"
        >
          <i class="pi pi-angle-right"></i>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed } from "vue";
import { storeToRefs } from "pinia";
import { Eye } from "lucide-vue";
import { useCalabozoStore } from "@/store/Pinia/useCalabozoStore";
import { useRouterCompat, useStoreCompat } from "@/composables";
const { route } = useRouterCompat();

const calabozoStore = useCalabozoStore();
const store = useStoreCompat();
const user = computed(() => store.state.user);
const tipoUsuario = computed(() => user.value.userdata.tipoUsuario);
const {
  paginatedDetenidos,
  isLoading,
  pageSize,
  totalDocs,
  offset,
  currentPage,
  totalPages,
  detenidos,
} = storeToRefs(calabozoStore);

const { handlePageChange, nextPage, prevPage, goToPage, setMockDetenidos, getDetenciones } =
  calabozoStore;

const PAGE_WINDOW_SIZE = 5;

const visiblePages = computed(() => {
  const total = totalPages.value;
  if (total <= 0) return [];
  const windowStart = Math.floor((currentPage.value - 1) / PAGE_WINDOW_SIZE) * PAGE_WINDOW_SIZE + 1;
  const windowEnd = Math.min(windowStart + PAGE_WINDOW_SIZE - 1, total);
  const pages = [];
  for (let i = windowStart; i <= windowEnd; i += 1) pages.push(i);
  return pages;
});

const canGoPrev = computed(() => currentPage.value > 1);
const canGoNext = computed(() => currentPage.value < totalPages.value);

const emit = defineEmits(["ver-detalle"]);

const verDetalle = (row) => {
  emit("ver-detalle", row);
};
const estadoLabels = {
  EN_CALABOZO: "En calabozo",
  LIBERADO: "Liberado",
  PENDIENTE: "Pendiente",
};
const estadoByTabIndex = ["EN_CALABOZO", "LIBERADO", "PENDIENTE"];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const estadoChipClass = (estado) => {
  if (!estado) return "";
  if (estado === "EN_CALABOZO") return "estado-chip--calabozo";
  if (estado === "PENDIENTE") return "estado-chip--pendiente";
  if (estado === "LIBERADO") return "estado-chip--liberado";
  return "";
};

onMounted(() => {
  const query = { ...route.query };

  calabozoStore.clearSelectedDetenido();
  const estado = estadoByTabIndex[query.tabIndex];

  if (estado) {
    calabozoStore.setEstadoFilter(estado);
  } else {
    calabozoStore.setEstadoFilter("EN_CALABOZO");
  }
  if (!query.id) getDetenciones();
});
</script>

<style scoped>
.seeButton {
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  color: #0b5195;
  border: 1px solid #0b5195;
  background-color: transparent;
}

.seeButton:hover {
  opacity: 0.9;
  cursor: pointer;
}

.center-screen {
  margin: 0 auto 1rem auto;
}

.border-gray {
  border: 1px solid #dfe4ea;
}

.selected {
  background-color: #3758f9;
  color: white;
}

.text-custom-gray {
  color: #637381;
}

.estado-chip {
  border-radius: 9999px;
  font-weight: 500;
}

.estado-chip--calabozo {
  background-color: #feebeb;
  color: #e10e0e;
}

.estado-chip--pendiente {
  background-color: #fffbeb;
  color: #d97706;
}

.estado-chip--liberado {
  background-color: #daf8e6;
  color: #1a8245;
}

.custom-focus:focus-within {
  border: 1.5px solid #3758f9;
}

::v-deep .p-datatable-thead .p-column-title {
  font-family: "IBM Plex Sans";
  font-weight: 600;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #000000;
}

::v-deep .p-datatable .p-column-header-content {
  justify-content: center;
}

::v-deep .p-datatable .p-datatable-tbody > tr > td {
  text-align: center;
  font-family: "Inter";
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  color: black;
}

::v-deep .p-datatable-table {
  table-layout: fixed;
}

::v-deep .p-datatable-wrapper .p-datatable-table {
  min-height: 100%;
}
</style>
